import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Grid, Paper, Tabs, Tab, Button, Fade } from '@mui/material';
import html2canvas from 'html2canvas';
import GoldenParticles from './components/GoldenParticles';

// assets内の画像をすべて読み込む
const images = require.context('./assets', false, /\.(png|PNG|jpg|jpeg)$/);
const imageFiles = images.keys().map((key) => ({
  file: key,
  src: images(key),
}));

// オープニング画像の取得
const openingImages = [
  images('./opening1.jpg'),
  images('./opening2.jpg'),
  images('./opening3.jpg'),
];

// パーツ名リスト
const PART_KEYS = [
  { label: '目', key: 'eye' },
  { label: '鼻', key: 'nose' },
  { label: '口', key: 'mouth' },
  { label: '髪', key: 'hair' },
  { label: '前髪', key: 'bang' },
  { label: '後ろ髪', key: 'backhair' },
  // 必要に応じて追加
];

// 接頭語ごとに分類
const PARTS = PART_KEYS.map(({ label, key }) => {
  const options = imageFiles
    .filter((img) => img.file.replace('./', '').toLowerCase().startsWith(key))
    .map((img, idx) => ({ id: idx + 1, name: img.file, img: img.src }));
  return { label, key, options };
});

// 最初に選択する画像（各パーツの最初の画像）
const getInitialSelected = () => {
  const selected = {};
  PARTS.forEach((part) => {
    selected[part.key] = part.options[0] || null;
  });
  return selected;
};

export default function App() {
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState(getInitialSelected());
  const previewRef = useRef(null);
  
  // アニメーション用のステート
  const [showImage1, setShowImage1] = useState(false);
  const [showImage2, setShowImage2] = useState(false);
  const [showImage3, setShowImage3] = useState(false);
  
  // オープニングアニメーション用のステート
  const [showOpening1, setShowOpening1] = useState(true);
  const [showOpening2, setShowOpening2] = useState(false);
  const [showOpening3, setShowOpening3] = useState(false);
  const [showMainContent, setShowMainContent] = useState(false);

  // オープニングアニメーション
  useEffect(() => {
    const opening2Timer = setTimeout(() => {
      setShowOpening1(false);
      setShowOpening2(true);
    }, 1500);

    const opening3Timer = setTimeout(() => {
      setShowOpening2(false);
      setShowOpening3(true);
    }, 3000);

    const mainContentTimer = setTimeout(() => {
      setShowOpening3(false);
      setShowMainContent(true);
    }, 4500);

    return () => {
      clearTimeout(opening2Timer);
      clearTimeout(opening3Timer);
      clearTimeout(mainContentTimer);
    };
  }, []);

  // メインコンテンツのアニメーション
  useEffect(() => {
    if (!showMainContent) return;
    
    const timer1 = setTimeout(() => setShowImage1(true), 500);
    const timer2 = setTimeout(() => setShowImage2(true), 1000);
    const timer3 = setTimeout(() => setShowImage3(true), 1500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [showMainContent]);

  const handleSelect = (partKey, part) => {
    setSelected((prev) => ({ ...prev, [partKey]: part }));
  };

  // プレビュー用の重ね順（必要に応じて調整）
  const previewOrder = ['backhair', 'hair', 'bang', 'eye', 'nose', 'mouth'];

  // ダウンロード処理
  const handleDownload = async () => {
    if (!previewRef.current) return;
    const canvas = await html2canvas(previewRef.current, { backgroundColor: null, useCORS: true, scale: 2 });
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'portrait.png';
    link.click();
  };

  return (
    <>
      {/* 背景の粒子アニメーション */}
      <Box sx={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 0
      }}>
        <GoldenParticles />
      </Box>

      <Container 
        maxWidth="sm" 
        sx={{ 
          pt: { xs: 2, sm: 6 }, 
          px: { xs: 1, sm: 2 },
          position: 'relative', 
          zIndex: 1 
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          width: '100%'
        }}>
          {/* オープニングアニメーション */}
          <Fade in={showOpening1} timeout={800}>
            <Box sx={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100vh', 
              zIndex: 9999,
              display: showOpening1 ? 'flex' : 'none',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fff'
            }}>
              <img src={openingImages[0]} alt="Opening 1" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            </Box>
          </Fade>
          <Fade in={showOpening2} timeout={800}>
            <Box sx={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100vh', 
              zIndex: 9999,
              display: showOpening2 ? 'flex' : 'none',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fff'
            }}>
              <img src={openingImages[1]} alt="Opening 2" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            </Box>
          </Fade>
          <Fade in={showOpening3} timeout={800}>
            <Box sx={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100vh', 
              zIndex: 9999,
              display: showOpening3 ? 'flex' : 'none',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fff'
            }}>
              <img src={openingImages[2]} alt="Opening 3" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            </Box>
          </Fade>

          {/* メインコンテンツ */}
          <Fade in={showMainContent} timeout={800}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* プレビュー */}
              <Paper 
                ref={previewRef} 
                elevation={4} 
                sx={{ 
                  width: { xs: '100%', sm: 260 }, 
                  height: { xs: 'calc(100vw - 32px)', sm: 260 }, 
                  maxWidth: 260,
                  maxHeight: 260,
                  mb: 2, 
                  position: 'relative', 
                  background: '#f8e0ea', 
                  borderRadius: 4, 
                  overflow: 'hidden',
                  mx: 'auto'
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: 1, height: 1 }}>
                  {/* 顔のベース画像があればここに */}
                  {imageFiles.find(img => img.file.replace('./', '').toLowerCase().startsWith('base')) && (
                    <Fade in={showImage1} timeout={800}>
                      <img
                        src={imageFiles.find(img => img.file.replace('./', '').toLowerCase().startsWith('base')).src}
                        alt="base"
                        style={{ 
                          position: 'absolute', 
                          width: '76%', 
                          height: '76%', 
                          left: '12%', 
                          top: '12%',
                          objectFit: 'contain'
                        }}
                      />
                    </Fade>
                  )}
                  {previewOrder.slice(0, 2).map((key) =>
                    selected[key] ? (
                      <Fade key={key} in={showImage2} timeout={800}>
                        <img
                          src={selected[key].img}
                          alt={key}
                          style={{ position: 'absolute', width: '200px', height: '200px', left: 30, top: 30 }}
                        />
                      </Fade>
                    ) : null
                  )}
                  {previewOrder.slice(2).map((key) =>
                    selected[key] ? (
                      <Fade key={key} in={showImage3} timeout={800}>
                        <img
                          src={selected[key].img}
                          alt={key}
                          style={{ position: 'absolute', width: '200px', height: '200px', left: 30, top: 30 }}
                        />
                      </Fade>
                    ) : null
                  )}
                </Box>
                <Box sx={{ width: 260, height: 260 }} />
              </Paper>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleDownload} 
                sx={{ 
                  mb: 2,
                  width: { xs: '100%', sm: 'auto' },
                  py: { xs: 1.5, sm: 1 }
                }}
              >
                ダウンロード
              </Button>
              {/* タブ */}
              <Tabs 
                value={tab} 
                onChange={(_, v) => setTab(v)} 
                variant="scrollable" 
                scrollButtons="auto" 
                sx={{ 
                  mb: 2, 
                  width: '100%',
                  '& .MuiTabs-scroller': {
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }
                }}
              >
                {PARTS.map((p) => (
                  <Tab 
                    key={p.key} 
                    label={p.label}
                    sx={{
                      minWidth: { xs: 'auto', sm: 120 },
                      px: { xs: 2, sm: 3 }
                    }}
                  />
                ))}
              </Tabs>
              {/* パーツ選択グリッド */}
              <Grid 
                container 
                spacing={{ xs: 1, sm: 2 }} 
                sx={{ 
                  width: '100%',
                  px: { xs: 1, sm: 0 }
                }}
              >
                {PARTS[tab].options.map((part) => (
                  <Grid item xs={4} sm={4} key={part.id}>
                    <Paper
                      elevation={selected[PARTS[tab].key]?.id === part.id ? 6 : 1}
                      sx={{ 
                        p: { xs: 0.5, sm: 1 }, 
                        textAlign: 'center', 
                        cursor: 'pointer', 
                        border: selected[PARTS[tab].key]?.id === part.id ? '2px solid #e66' : '1px solid #ccc',
                        '&:hover': {
                          backgroundColor: 'rgba(238, 102, 102, 0.1)'
                        }
                      }}
                      onClick={() => handleSelect(PARTS[tab].key, part)}
                    >
                      <Box
                        component="img"
                        src={part.img}
                        alt={part.name}
                        sx={{
                          width: { xs: 40, sm: 60 },
                          height: { xs: 40, sm: 60 },
                          objectFit: 'contain'
                        }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        </Box>
      </Container>
    </>
  );
} 